import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  useMediaQuery,
  useTheme,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { can } from '../../utils/roleUtils';
import ApiService from '../../services/api.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';

const TOOLBAR_SX = {
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

const Quotations = ({ currentUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    typingCenterId: '',
    organizationId: '',
    groupId: ''
  });
  // Customer groups for the group filter — only shown when this TC actually has groups.
  const [orgGroups, setOrgGroups] = useState([]);
  useEffect(() => {
    let active = true;
    ApiService.getPortalCustomerGroups({ size: 100 })
      .then((res) => { if (active) setOrgGroups(res?.data?.data || res?.data || []); })
      .catch(() => { if (active) setOrgGroups([]); });
    return () => { active = false; };
  }, []);

  // Date range state
  const [dateRange, setDateRange] = useState('30days');
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Date range options
  const dateRangeOptions = [
    { value: '24hr', label: '24h' },
    { value: '7days', label: '7 days' },
    { value: '30days', label: '30 days' },
    { value: '90days', label: '90 days' },
    { value: 'custom', label: 'Custom' }
  ];

  // Calculate date range
  const getDateRange = () => {
    const endDate = new Date();
    let startDate = new Date();

    switch (dateRange) {
      case '24hr':
        startDate.setDate(startDate.getDate() - 1);
        break;
      case '7days':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30days':
        startDate.setDate(startDate.getDate() - 30);
        break;
      case '90days':
        startDate.setDate(startDate.getDate() - 90);
        break;
      case 'custom':
        return {
          startDate: customStartDate ? new Date(customStartDate).toISOString() : null,
          endDate: customEndDate ? new Date(customEndDate).toISOString() : null
        };
      default:
        startDate.setDate(startDate.getDate() - 30);
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  };

  // Fetch quotations
  useEffect(() => {
    fetchQuotations();
    fetchStatistics();
  }, [filters.status, filters.groupId, dateRange, page, rowsPerPage, customStartDate, customEndDate]);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(filters.status && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
        ...(filters.groupId && { groupId: filters.groupId }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate })
      };

      const response = await ApiService.getQuotations(params);
      const data = response?.data?.data || response?.data || [];
      setQuotations(Array.isArray(data) ? data : []);
      setTotalCount(response?.data?.total || data.length || 0);
    } catch (err) {
      console.error('Error fetching quotations:', err);
      setError('Failed to load quotations');
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await ApiService.getQuotationStatistics(null, getDateRange());
      setStatistics(response?.data?.data || response?.data || null);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchQuotations();
  };

  const handleDateRangeChange = (value) => {
    if (value === 'custom') {
      setCustomDateOpen(true);
    } else {
      setDateRange(value);
      setPage(0);
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setDateRange('custom');
      setCustomDateOpen(false);
      setPage(0);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteQuotation = async (quotationId) => {
    const confirmed = window.confirm('Are you sure you want to delete this draft quotation?');
    if (!confirmed) return;

    try {
      setLoading(true);
      await ApiService.deleteQuotation(quotationId);
      setQuotations(prev => prev.filter(q => q.id !== quotationId));
      fetchStatistics();
    } catch (err) {
      console.error('Error deleting quotation:', err);
      setError('Failed to delete quotation');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async (quotationId) => {
    try {
      const response = await ApiService.getQuotationPdfView(quotationId);
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error('Error opening quotation PDF:', err);
      setError('Failed to open PDF');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { color: 'warning', label: 'Draft' },
      FINAL: { color: 'primary', label: 'Final' },
      SENT: { color: 'info', label: 'Sent' },
      ACCEPTED: { color: 'success', label: 'Accepted' },
      REJECTED: { color: 'error', label: 'Rejected' },
      EXPIRED: { color: 'error', label: 'Expired', variant: 'outlined' },
      CONVERTED: { color: 'secondary', label: 'Converted' }
    };
    const config = statusConfig[status] || { color: 'info', label: status, variant: 'outlined' };
    return <Chip label={config.label} color={config.color} variant={config.variant} size="small" />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getQuotationNumber = (quotation) => {
    return quotation.quotation_number || quotation.draft_number || '-';
  };

  return (
    <Box sx={{ pb: isMobile ? 2 : 0, width: 0, minWidth: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <ProTableStats
        loading={!statistics}
        cards={[
          { key: '', label: 'Total', value: statistics?.total || 0 },
          { key: 'DRAFT', label: 'Drafts', value: statistics?.draft_count || 0, color: 'warning.main' },
          { key: 'SENT', label: 'Sent', value: statistics?.sent_count || 0, color: 'info.main' },
          { key: 'ACCEPTED', label: 'Accepted', value: statistics?.accepted_count || 0, color: 'success.main' },
        ].map((c) => ({
          ...c,
          highlight: filters.status === c.key,
          onClick: () => { setPage(0); setFilters((prev) => ({ ...prev, status: c.key })); },
        }))}
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5, ...TOOLBAR_SX }}>
        <TextField
          size="small"
          placeholder={isMobile ? "Search..." : "Search by quotation number or organization..."}
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ flex: '1 1 200px', minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            label="Status"
            onChange={(e) => { setPage(0); setFilters(prev => ({ ...prev, status: e.target.value })); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="SENT">Sent</MenuItem>
            <MenuItem value="ACCEPTED">Accepted</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
          </Select>
        </FormControl>
        {orgGroups.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Group</InputLabel>
            <Select
              value={filters.groupId}
              label="Group"
              onChange={(e) => { setPage(0); setFilters(prev => ({ ...prev, groupId: e.target.value })); }}
            >
              <MenuItem value="">All Groups</MenuItem>
              {orgGroups.map((g) => (
                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={dateRange}
          onChange={(e, v) => { if (v) handleDateRangeChange(v); }}
          sx={{
            '& .MuiToggleButton-root': { height: 32, px: 1.25, fontSize: '0.75rem', textTransform: 'none' },
            '& .MuiToggleButton-root.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } },
          }}
        >
          {dateRangeOptions.map((option) => (
            <ToggleButton key={option.value} value={option.value}
              onClick={option.value === 'custom' && dateRange === 'custom' ? () => setCustomDateOpen(true) : undefined}>
              {option.value === 'custom' && dateRange === 'custom' && customStartDate && customEndDate
                ? `${formatDate(customStartDate)} – ${formatDate(customEndDate)}`
                : option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {totalCount} quotations
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchQuotations} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {can(currentUser, 'quotation', 'write') && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/quotations/create')}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', ml: 0.5 }}
            >
              Create Quotation
            </Button>
          )}
        </Box>
      </Box>

      {/* Quotations - Mobile Cards or Desktop Table */}
      {isMobile ? (
        /* Mobile Card View */
        <Stack spacing={1.5}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : quotations.length === 0 ? (
            <Paper sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No quotations found</Typography>
            </Paper>
          ) : (
            quotations.map((quotation) => (
              <Card
                key={quotation.id}
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 2 },
                  '&:active': { bgcolor: 'action.selected' }
                }}
                onClick={() => navigate(`/quotations/${quotation.id}`)}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box>
                      <Typography fontWeight={600} color="primary">
                        {getQuotationNumber(quotation)}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {quotation.organization_name}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getStatusChip(quotation.status)}
                      {quotation.status === 'DRAFT' && can(currentUser, 'quotation', 'delete') && (
                        <Tooltip title="Delete Draft">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteQuotation(quotation.id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <ChevronRightIcon color="action" sx={{ fontSize: 20 }} />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" gap={2}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(quotation.quotation_date)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Valid: {formatDate(quotation.valid_until)}
                      </Typography>
                    </Box>
                    <Typography fontWeight={700} color="primary" sx={{ fontSize: '1rem' }}>
                      {formatCurrency(quotation.grand_total)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>
      ) : (
        /* Desktop Table View */
        <Paper>
          <TableContainer>
            {/* Same sizes as the Tasks table: body 13px, header 12px semibold, compact rows */}
            <Table size="small" sx={{
              '& .MuiTableCell-head': { fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' },
              '& .MuiTableCell-body': { fontSize: '0.8125rem', py: 0.75, lineHeight: 1.4 },
              '& .MuiTableCell-body .MuiTypography-root': { fontSize: 'inherit' },
              '& .MuiTableCell-body .MuiTypography-caption': { fontSize: '0.75rem' },
              '& .MuiChip-root': { fontSize: '0.75rem', height: 22 },
            }}>
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  <TableCell>Quotation #</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Valid Until</TableCell>
                  <TableCell align="center">Items</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : quotations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No quotations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  quotations.map((quotation) => (
                    <TableRow key={quotation.id} hover>
                      <TableCell>
                        <Typography fontWeight={600} color="primary">
                          {getQuotationNumber(quotation)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {quotation.organization_name}
                        </Typography>
                        {quotation.organization_code && (
                          <Typography variant="caption" color="text.secondary">
                            {quotation.organization_code}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(quotation.quotation_date)}</TableCell>
                      <TableCell>{formatDate(quotation.valid_until)}</TableCell>
                      <TableCell align="center">{quotation.item_count}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          {formatCurrency(quotation.grand_total)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {getStatusChip(quotation.status)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Quotation">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/quotations/${quotation.id}`)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {quotation.status !== 'DRAFT' && (
                          <Tooltip title="View PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleViewPdf(quotation.id)}
                            >
                              <PdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {quotation.status === 'DRAFT' && can(currentUser, 'quotation', 'delete') && (
                          <Tooltip title="Delete Draft">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteQuotation(quotation.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* Mobile Pagination */}
      {isMobile && quotations.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Per page:"
          />
        </Box>
      )}

      {/* Custom Date Range Dialog */}
      <Dialog open={customDateOpen} onClose={() => setCustomDateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCustomDateApply}
            disabled={!customStartDate || !customEndDate}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Quotations;
